import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { App, Button, Card, Empty, Input, InputNumber, Modal, Select, Space, Typography } from 'antd';
import { useMemo, useState } from 'react';
import type { ManagedProjectOption, ProjectWorkItemFormValue } from '@/types/report-project-work-item-lite';
import {
  buildSelectedProjectIdSet,
  compactProjectWorkItemSortOrder,
  createProjectWorkItemFromOption,
  filterSelectableManagedProjects,
  nextProjectWorkItemSortOrder,
} from '@/utils/report-project-work-item-lite';

export interface ReportProjectWorkItemsSectionProps {
  isMobile?: boolean;
  disabled?: boolean;
  managedProjects: ManagedProjectOption[];
  loadingProjects?: boolean;
  items: ProjectWorkItemFormValue[];
  onChange: (items: ProjectWorkItemFormValue[]) => void;
}

function sortedItems(items: ProjectWorkItemFormValue[]): ProjectWorkItemFormValue[] {
  return [...items].sort((a, b) => a.sortOrder - b.sortOrder);
}

function updateItem(
  items: ProjectWorkItemFormValue[],
  clientKey: string,
  patch: Partial<ProjectWorkItemFormValue>,
): ProjectWorkItemFormValue[] {
  return items.map((item) => (item.clientKey === clientKey ? { ...item, ...patch } : item));
}

function ProjectWorkItemFields({
  item,
  disabled,
  isMobile,
  onPatch,
  onRemove,
}: {
  item: ProjectWorkItemFormValue;
  disabled?: boolean;
  isMobile?: boolean;
  onPatch: (patch: Partial<ProjectWorkItemFormValue>) => void;
  onRemove: () => void;
}) {
  if (isMobile) {
    return (
      <div className="report-project-work-item">
        <div className="report-project-work-item__header">
          <span className="report-project-work-item__title">{item.projectName}</span>
          {!disabled ? (
            <button type="button" className="report-project-work-item__remove" onClick={onRemove}>
              移除
            </button>
          ) : null}
        </div>
        <label className="mobile-form-field">
          <span className="mobile-form-field__label">汇报进度（%）</span>
          <input
            className="mobile-form-field__input"
            type="number"
            min={0}
            max={100}
            disabled={disabled}
            value={item.progressPercent}
            onChange={(event) => onPatch({ progressPercent: Number(event.target.value) })}
          />
        </label>
        <label className="mobile-form-field">
          <span className="mobile-form-field__label">项目进展 *</span>
          <textarea
            className="mobile-comment-compose__input"
            rows={3}
            disabled={disabled}
            value={item.progressContent}
            onChange={(event) => onPatch({ progressContent: event.target.value })}
            placeholder="本期项目进展"
          />
        </label>
        <label className="mobile-form-field">
          <span className="mobile-form-field__label">项目问题</span>
          <textarea
            className="mobile-comment-compose__input"
            rows={2}
            disabled={disabled}
            value={item.problemContent ?? ''}
            onChange={(event) => onPatch({ problemContent: event.target.value })}
            placeholder="可选"
          />
        </label>
        <label className="mobile-form-field">
          <span className="mobile-form-field__label">下一步计划</span>
          <textarea
            className="mobile-comment-compose__input"
            rows={2}
            disabled={disabled}
            value={item.nextPlanContent ?? ''}
            onChange={(event) => onPatch({ nextPlanContent: event.target.value })}
            placeholder="可选"
          />
        </label>
      </div>
    );
  }

  return (
    <Card
      size="small"
      className="report-project-work-item report-project-work-item--desktop"
      title={item.projectName}
      extra={
        disabled ? null : (
          <Button type="text" danger icon={<DeleteOutlined />} onClick={onRemove}>
            移除
          </Button>
        )
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Typography.Text type="secondary">汇报进度（%）</Typography.Text>
          <InputNumber
            min={0}
            max={100}
            disabled={disabled}
            value={item.progressPercent}
            onChange={(value) => onPatch({ progressPercent: value ?? 0 })}
            style={{ width: '100%', marginTop: 4 }}
          />
        </div>
        <div>
          <Typography.Text type="secondary">项目进展 *</Typography.Text>
          <Input.TextArea
            rows={3}
            disabled={disabled}
            value={item.progressContent}
            onChange={(event) => onPatch({ progressContent: event.target.value })}
            placeholder="本期项目进展"
            maxLength={10000}
            showCount
          />
        </div>
        <div>
          <Typography.Text type="secondary">项目问题</Typography.Text>
          <Input.TextArea
            rows={2}
            disabled={disabled}
            value={item.problemContent ?? ''}
            onChange={(event) => onPatch({ problemContent: event.target.value })}
            placeholder="可选"
            maxLength={10000}
          />
        </div>
        <div>
          <Typography.Text type="secondary">下一步计划</Typography.Text>
          <Input.TextArea
            rows={2}
            disabled={disabled}
            value={item.nextPlanContent ?? ''}
            onChange={(event) => onPatch({ nextPlanContent: event.target.value })}
            placeholder="可选"
            maxLength={10000}
          />
        </div>
      </Space>
    </Card>
  );
}

export function ReportProjectWorkItemsSection({
  isMobile = false,
  disabled = false,
  managedProjects,
  loadingProjects = false,
  items,
  onChange,
}: ReportProjectWorkItemsSectionProps) {
  const { message } = App.useApp();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingProjectId, setPendingProjectId] = useState<string | null>(null);

  const selectedProjectIds = useMemo(() => buildSelectedProjectIdSet(items), [items]);
  const selectableProjects = useMemo(
    () => filterSelectableManagedProjects(managedProjects, selectedProjectIds),
    [managedProjects, selectedProjectIds],
  );
  const orderedItems = useMemo(() => sortedItems(items), [items]);

  const handleAddProject = (projectId: string) => {
    const project = managedProjects.find((entry) => entry.id === projectId);
    if (!project) {
      return;
    }
    if (selectedProjectIds.has(projectId)) {
      message.warning('该项目已添加');
      return;
    }
    const nextItem = createProjectWorkItemFromOption(project, nextProjectWorkItemSortOrder(items));
    onChange([...items, nextItem]);
    setPendingProjectId(null);
    setPickerOpen(false);
  };

  const handleOpenPicker = () => {
    if (selectableProjects.length === 0) {
      message.info('没有可添加的项目');
      return;
    }
    setPendingProjectId(selectableProjects[0]?.id ?? null);
    setPickerOpen(true);
  };

  const handleRemove = (clientKey: string) => {
    onChange(compactProjectWorkItemSortOrder(items.filter((item) => item.clientKey !== clientKey)));
  };

  const addButton = (
    <Button
      type="dashed"
      icon={<PlusOutlined />}
      block={isMobile}
      disabled={disabled || loadingProjects || selectableProjects.length === 0}
      onClick={() => {
        if (isMobile) {
          handleOpenPicker();
        } else if (selectableProjects.length === 1 && selectableProjects[0]) {
          handleAddProject(selectableProjects[0].id);
        } else {
          handleOpenPicker();
        }
      }}
    >
      添加项目
    </Button>
  );

  return (
    <div className="report-project-work-items-section">
      <div className="report-project-work-items-section__header">
        <Typography.Text strong={!isMobile}>项目进展</Typography.Text>
        <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
          填写您负责项目的当日进展（与上方个人工作内容一并提交）
        </Typography.Text>
      </div>

      {orderedItems.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={loadingProjects ? '加载项目中…' : '暂未添加项目，可点击下方按钮添加'}
          style={{ margin: '12px 0' }}
        />
      ) : (
        <div className="report-project-work-items-section__list">
          {orderedItems.map((item) => (
            <ProjectWorkItemFields
              key={item.clientKey}
              item={item}
              isMobile={isMobile}
              disabled={disabled}
              onPatch={(patch) => onChange(updateItem(items, item.clientKey, patch))}
              onRemove={() => handleRemove(item.clientKey)}
            />
          ))}
        </div>
      )}

      <div className="report-project-work-items-section__actions">{addButton}</div>

      <Modal
        title="选择项目"
        open={pickerOpen}
        onCancel={() => setPickerOpen(false)}
        onOk={() => {
          if (pendingProjectId) {
            handleAddProject(pendingProjectId);
          }
        }}
        okText="添加"
        cancelText="取消"
        destroyOnClose
      >
        <Select
          style={{ width: '100%' }}
          placeholder="请选择项目"
          value={pendingProjectId ?? undefined}
          onChange={setPendingProjectId}
          options={selectableProjects.map((project) => ({
            value: project.id,
            label: `${project.name}（${project.projectNo}）`,
          }))}
        />
      </Modal>
    </div>
  );
}
